import { MouseEventHandler } from "react"

import styled from "styled-components"

import { ReactComponent as DropDownMinus } from "images/button-icons/dropDownMinus.svg"
import { ReactComponent as DropDownPlus } from "images/button-icons/dropDownPlus.svg"
import { ReactComponent as ArrowRightSVG } from "images/global/arrow.svg"
import { ReactComponent as CheckmarkSVG } from "images/global/checkmark.svg"
import { ReactComponent as ChevronSVGDown } from "images/global/chevron-down-s.svg"
import { ReactComponent as ChevronSVGUp } from "images/global/chevron-up-s.svg"
import { ReactComponent as ArrowDownSVG } from "images/global/down-arrow.svg"
import { ReactComponent as ExpandSVG } from "images/global/expand.svg"
import { ReactComponent as XSVG } from "images/global/x.svg"
import colors from "styles/colors"
import easing from "styles/easing"
import media from "styles/media"
import text from "styles/text"
import { CustomRoundButtonColors } from "types"

export default function RoundButton({
  large = false,
  small = false,
  right = false,
  left = false,
  ex = false,
  checkmark = false,
  blue = false,
  chevronUp = false,
  chevronDown = false,
  flip = false,
  onClick = () => null,
  disabled = false,
  className = "",
  expand = false,
  customColors = undefined,
  dropDown = false,
  dropDownOpen = false,
}: {
  large?: boolean
  small?: boolean
  right?: boolean
  left?: boolean
  ex?: boolean
  checkmark?: boolean
  blue?: boolean
  chevronDown?: boolean
  chevronUp?: boolean
  flip?: boolean
  onClick?: MouseEventHandler | null
  disabled?: boolean
  expand?: boolean
  className?: string
  customColors?: CustomRoundButtonColors
  dropDown?: boolean
  dropDownOpen?: boolean
}) {
  let stateColors: {
    background?: {
      default?: string
      hover?: string
      active?: string
      disabled?: string
    }
    svg?: {
      default?: string
      hover?: string
      active?: string
      disabled?: string
    }
  }

  switch (true) {
    case small && right:
      stateColors = {
        background: {
          default: colors.blue10,
          hover: colors.blue400,
          active: colors.blue500,
        },
        svg: {
          default: colors.blue400,
          hover: colors.white,
          active: colors.white,
        },
      }

      break
    case blue:
      stateColors = {
        background: {
          default: colors.blue400,
          hover: colors.blue400,
          active: colors.blue500,
          disabled: colors.blue50,
        },
        svg: {
          default: colors.white,
          hover: colors.white,
          active: colors.white,
        },
      }

      break

    case customColors && customColors !== undefined:
      stateColors = {
        background: {
          default: customColors?.background?.default,
          hover: customColors?.background?.hover,
          active: customColors?.background?.active,
          disabled: customColors?.background?.disabled,
        },
        svg: {
          default: customColors?.svg?.default,
          hover: customColors?.svg?.hover,
          active: customColors?.svg?.active,
          disabled: customColors?.svg?.disabled,
        },
      }

      break

    case small && ex:
      stateColors = {
        background: {
          default: colors.coral200,
          hover: colors.coral400,
          active: colors.coral600,
        },
        svg: {
          default: colors.white,
          hover: colors.white,
          active: colors.white,
        },
      }

      break

    case small && checkmark:
      stateColors = {
        background: {
          default: colors.seaFoam300,
          hover: colors.seaFoam400,
          active: colors.seaFoam600,
        },
        svg: {
          default: colors.white,
          hover: colors.white,
          active: colors.white,
        },
      }

      break

    default:
      stateColors = {
        background: {
          default: colors.white,
          hover: colors.blue400,
          active: colors.blue500,
        },
        svg: {
          default: colors.blue400,
          hover: colors.white,
          active: colors.white,
        },
      }
      break
  }

  return (
    <Wrapper
      aria-label="round button"
      large={large}
      small={small}
      right={right}
      left={left}
      ex={ex}
      className={className}
      expand={expand}
      checkmark={checkmark}
      stateColors={stateColors}
      onClick={onClick || (() => null)}
      disabled={disabled}
      flip={flip}
    >
      {dropDown ? (
        dropDownOpen ? (
          <DropDownMinus />
        ) : (
          <DropDownPlus />
        )
      ) : right ? (
        <ArrowRightSVG />
      ) : chevronDown ? (
        <ChevronSVGDown />
      ) : chevronUp ? (
        <ChevronSVGUp />
      ) : ex ? (
        <XSVG />
      ) : checkmark ? (
        <CheckmarkSVG />
      ) : expand ? (
        <ExpandSVG />
      ) : (
        <ArrowDownSVG />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.button<{
  large?: boolean
  small?: boolean
  right?: boolean
  left?: boolean
  ex?: boolean | string
  checkmark?: boolean
  onClick: MouseEventHandler
  disabled?: boolean
  flip?: boolean
  expand?: boolean
  stateColors: {
    background?: {
      default?: string
      hover?: string
      active?: string
      disabled?: string
    }
    svg?: {
      default?: string
      hover?: string
      active?: string
      disabled?: string
    }
  }
}>`
  transition-duration: 300ms;
  transition-property: background;
  transition-timing-function: ${easing.button};
  background: none;
  ${text.bodyL};
  border: none;
  width: ${({ small, large, expand }) => {
    switch (true) {
      case small:
        return `2.108vw`

      case large:
        return `6.627vw`

      case expand:
        return `2.169vw`

      default:
        return "3.313vw"
    }
  }};
  height: ${({ small, large }) => {
    switch (true) {
      case small:
        return `2.108vw`

      case large:
        return `6.627vw`

      default:
        return "3.313vw"
    }
  }};
  aspect-ratio: 1;
  background-color: ${({ stateColors, disabled }) =>
    disabled
      ? stateColors.background?.disabled
      : stateColors.background?.default};
  ${media.hover} {
    :hover {
      background-color: ${({ stateColors, disabled }) =>
        disabled
          ? stateColors.background?.disabled
          : stateColors.background?.hover};
      path {
        ${({ ex, checkmark, expand, stateColors }) =>
          ex || checkmark || expand
            ? `stroke:${stateColors.svg?.hover}`
            : `fill:${stateColors.svg?.hover}`};
      }
    }
  }
  :active {
    background-color: ${({ stateColors, disabled }) =>
      disabled
        ? stateColors.background?.disabled
        : stateColors.background?.active};
    path {
      ${({ ex, checkmark, stateColors }) =>
        ex || checkmark
          ? `stroke:${stateColors.svg?.active}` ?? colors.white
          : `fill:${stateColors.svg?.active}` ?? colors.white};
    }
  }
  svg {
    transform: rotateZ(
      ${props =>
        props.expand ? -45 : props.left ? 90 : props.flip ? 180 : 0}deg
    );
    transition-timing-function: cubic-bezier(${easing.main});
    transition-duration: 300ms;
    width: ${({ small, large, expand }) =>
      small ? "0.904vw" : expand ? "2.169vw" : large ? "3.012vw" : "1.506vw"};
    height: ${({ small, large, expand }) =>
      small ? "0.904vw" : expand ? "2.169vw" : large ? "3.012vw" : "1.506vw"};
    aspect-ratio: 1;
    path {
      transition-duration: 300ms;
      transition-property: stroke, fill;
      transition-timing-function: ${easing.button};
      ${({ ex, checkmark, stateColors, expand }) =>
        ex || checkmark || expand
          ? `stroke:${stateColors.svg?.default}`
          : `fill:${stateColors.svg?.default}`};
    }
  }
  :disabled {
    svg {
      path {
        ${({ ex, checkmark, expand, stateColors }) =>
          ex || checkmark || expand
            ? `stroke: ${stateColors.svg?.disabled};`
            : `fill: ${stateColors.svg?.disabled};`}
      }
    }
  }

  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  ${media.fullWidth} {
    width: ${({ small, large }) => {
      switch (true) {
        case small:
          return `35px`

        case large:
          return `110px`

        default:
          return "55px"
      }
    }};
    height: ${({ small, large }) => {
      switch (true) {
        case small:
          return `35px`

        case large:
          return `110px`

        default:
          return "55px"
      }
    }};
    svg {
      width: ${({ small, large }) =>
        small ? "15px" : large ? "50px" : "25px"};
      height: ${({ small, large }) =>
        small ? "15px" : large ? "50px" : "25px"};
    }
  }
  ${media.tablet} {
    width: ${({ small, large }) => {
      switch (true) {
        case small:
          return `3.418vw`

        case large:
          return `10.742vw`

        default:
          return "5.371vw"
      }
    }};
    height: ${({ small, large }) => {
      switch (true) {
        case small:
          return `3.418vw`

        case large:
          return `10.742vw`

        default:
          return "5.371vw"
      }
    }};
    svg {
      width: ${({ small, large }) =>
        small ? "1.465vw" : large ? "4.883vw" : "2.441vw"};
      height: ${({ small, large }) =>
        small ? "1.465vw" : large ? "4.883vw" : "2.441vw"};
    }
  }
  ${media.mobile} {
    width: ${({ small, large }) => {
      switch (true) {
        case small:
          return `9.333vw`

        case large:
          return `29.333vw`

        default:
          return "14.667vw"
      }
    }};
    height: ${({ small, large }) => {
      switch (true) {
        case small:
          return `9.333vw`

        case large:
          return `29.333vw`

        default:
          return "14.667vw"
      }
    }};
    svg {
      width: ${({ small, large }) =>
        small ? "4vw" : large ? "13.333vw" : "6.667vw"};
      height: ${({ small, large }) =>
        small ? "4vw" : large ? "13.333vw" : "6.667vw"};
    }
  }
`
